import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { usePlans, useDeletePlan } from '../../../hooks/subscriptions';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { formatPrice, formatBillingInterval, formatResourceLimit } from '../../../utils/subscriptions';
import type { SubscriptionPlan } from '../../../types/subscriptions';

export const PlanList = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);

  const { data: plans, isLoading } = usePlans();
  const deleteMutation = useDeletePlan();

  const handleDeleteClick = (id: string) => {
    setPlanToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (planToDelete) {
      deleteMutation.mutate(planToDelete, {
        onSuccess: () => {
          showMessage('Plan deleted successfully', 'success');
          setDeleteDialogOpen(false);
          setPlanToDelete(null);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to delete plan', 'error');
        },
      });
    }
  };

  const columns: GridColDef<SubscriptionPlan>[] = [
    {
      field: 'name',
      headerName: 'Plan Name',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.slug}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'basePrice',
      headerName: 'Price',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {formatPrice(params.value, params.row.currency)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatBillingInterval(params.row.billingInterval)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'maxUsers',
      headerName: 'Max Users',
      width: 120,
      renderCell: (params) => formatResourceLimit(params.value),
    },
    {
      field: 'maxStores',
      headerName: 'Max Stores',
      width: 120,
      renderCell: (params) => formatResourceLimit(params.value),
    },
    {
      field: 'maxWarehouses',
      headerName: 'Max Warehouses',
      width: 150,
      renderCell: (params) => formatResourceLimit(params.value),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/subscriptions/plans/show/${params.row.id}`)}
            title="View details"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => navigate(`/subscriptions/plans/edit/${params.row.id}`)}
            title="Edit plan"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(params.row.id)}
            title="Delete plan"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Subscription Plans</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage subscription plans and pricing tiers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/subscriptions/plans/create')}
        >
          Create Plan
        </Button>
      </Box>

      <Card>
        <CardContent>
          <DataGrid
            rows={plans || []}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: {
                sortModel: [{ field: 'displayOrder', sort: 'asc' }],
              },
            }}
            autoHeight
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1.5,
              },
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this subscription plan? This action cannot be undone.
            Organizations using this plan may be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
